import { useMemo } from 'react';
import { BlogPost } from '../utils/browserXMLParser';

export interface BlogFilterOptions {
  destination?: string;
  type?: string;
  search?: string;
}

export interface UseBlogFilteringResult {
  filteredPosts: BlogPost[];
  totalResults: number;
  availableDestinations: string[];
  availableTypes: string[];
}

/**
 * Enhanced blog filtering hook that filters XML blog posts based on categories and destinations
 * Maps WordPress categories to filter destinations and types
 */
export const useBlogFiltering = (
  posts: BlogPost[],
  filters: BlogFilterOptions
): UseBlogFilteringResult => {
  
  const filteredPosts = useMemo(() => {
    let filtered = [...posts];

    // Filter by destination
    if (filters.destination) {
      filtered = filtered.filter(post => {
        const destination = filters.destination!.toLowerCase();
        
        // Check categories for destination match
        const hasDestinationCategory = post.categories.some(cat => 
          cat.toLowerCase().includes(destination) ||
          destination.includes(cat.toLowerCase())
        );
        
        // Check title and content for destination mentions
        const hasDestinationMention = 
          post.title.toLowerCase().includes(destination) ||
          post.content.toLowerCase().includes(destination) ||
          post.excerpt.toLowerCase().includes(destination);
        
        return hasDestinationCategory || hasDestinationMention;
      });
    }

    // Filter by type/category
    if (filters.type) {
      filtered = filtered.filter(post => {
        const type = filters.type!.toLowerCase();
        
        // Map filter types to WordPress categories
        const categoryMappings: { [key: string]: string[] } = {
          'adventure': ['adventure'],
          'culture': ['culture'],
          'wildlife': ['wildlife', 'fauna'],
          'foodie': ['foodie'],
          'art': ['art', 'awasi artist immersion'],
          'awasi-family': ['awasi family'],
          'foundation': ['foundation', 'puma foundation'],
          'community': ['community', 'awasi foundation community'],
          'awasi-artist-immersion': ['awasi artist immersion', 'art'],
          'travel-inspiration': ['travel inspiration']
        };
        
        const mappedCategories = categoryMappings[type] || [type];
        
        return post.categories.some(cat => 
          mappedCategories.some(mapped => 
            cat.toLowerCase().includes(mapped) || 
            mapped.includes(cat.toLowerCase())
          )
        ) || post.tags.some(tag => 
          mappedCategories.some(mapped => 
            tag.toLowerCase().includes(mapped) || 
            mapped.includes(tag.toLowerCase())
          )
        );
      });
    }

    // Filter by search term
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(searchTerm) ||
        post.content.toLowerCase().includes(searchTerm) ||
        post.excerpt.toLowerCase().includes(searchTerm) ||
        post.author.toLowerCase().includes(searchTerm) ||
        post.categories.some(cat => cat.toLowerCase().includes(searchTerm)) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm))
      );
    }

    return filtered;
  }, [posts, filters]);

  // Extract available destinations from all posts
  const availableDestinations = useMemo(() => {
    const destinations = new Set<string>();
    
    posts.forEach(post => {
      // Check categories for destination names
      post.categories.forEach(cat => {
        const lowerCat = cat.toLowerCase();
        if (lowerCat.includes('patagonia')) destinations.add('patagonia');
        if (lowerCat.includes('atacama')) destinations.add('atacama');
        if (lowerCat.includes('iguazu') || lowerCat.includes('iguazú')) destinations.add('iguazu');
        if (lowerCat.includes('mendoza')) destinations.add('mendoza');
        if (lowerCat.includes('santa catarina')) destinations.add('santa-catarina');
      });
      
      // Check content for destination mentions
      const content = `${post.title} ${post.content} ${post.excerpt}`.toLowerCase();
      if (content.includes('patagonia')) destinations.add('patagonia');
      if (content.includes('atacama')) destinations.add('atacama');
      if (content.includes('iguazu') || content.includes('iguazú')) destinations.add('iguazu');
      if (content.includes('mendoza')) destinations.add('mendoza');
      if (content.includes('santa catarina')) destinations.add('santa-catarina');
    });
    
    return Array.from(destinations);
  }, [posts]);

  // Extract available types from all posts
  const availableTypes = useMemo(() => {
    const types = new Set<string>();
    
    posts.forEach(post => {
      post.categories.forEach(cat => {
        const lowerCat = cat.toLowerCase();
        if (lowerCat.includes('adventure')) types.add('adventure');
        if (lowerCat.includes('culture')) types.add('culture');
        if (lowerCat.includes('wildlife') || lowerCat.includes('fauna')) types.add('wildlife');
        if (lowerCat.includes('foodie')) types.add('foodie');
        if (lowerCat.includes('art')) types.add('art');
        if (lowerCat.includes('awasi family')) types.add('awasi-family');
        if (lowerCat.includes('foundation')) types.add('foundation');
        if (lowerCat.includes('community')) types.add('community');
        if (lowerCat.includes('artist immersion')) types.add('awasi-artist-immersion');
        if (lowerCat.includes('travel inspiration')) types.add('travel-inspiration');
      });
    });
    
    return Array.from(types);
  }, [posts]);

  return {
    filteredPosts,
    totalResults: filteredPosts.length,
    availableDestinations,
    availableTypes
  };
};

/**
 * Helper hook for property-specific blog filtering
 * Automatically filters posts for a specific property
 */
export const usePropertyBlogFiltering = (
  posts: BlogPost[],
  property: string,
  additionalFilters: Omit<BlogFilterOptions, 'destination'> = {}
): UseBlogFilteringResult => {
  const filters: BlogFilterOptions = {
    destination: property,
    ...additionalFilters
  };

  return useBlogFiltering(posts, filters);
};

/**
 * Helper to get blog posts count by destination
 */
export const getBlogPostCounts = (posts: BlogPost[]) => {
  const counts: { [key: string]: number } = {
    patagonia: 0,
    atacama: 0,
    iguazu: 0,
    mendoza: 0,
    'santa-catarina': 0
  };

  posts.forEach(post => {
    const content = `${post.title} ${post.content} ${post.excerpt}`.toLowerCase();
    post.categories.forEach(cat => {
      const lowerCat = cat.toLowerCase();
      if (lowerCat.includes('patagonia') || content.includes('patagonia')) counts.patagonia++;
      if (lowerCat.includes('atacama') || content.includes('atacama')) counts.atacama++;
      if (lowerCat.includes('iguazu') || lowerCat.includes('iguazú') || 
          content.includes('iguazu') || content.includes('iguazú')) counts.iguazu++;
      if (lowerCat.includes('mendoza') || content.includes('mendoza')) counts.mendoza++;
      if (lowerCat.includes('santa catarina') || content.includes('santa catarina')) counts['santa-catarina']++;
    });
  });

  return counts;
};